import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../api/axios';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useToastContext } from '../contexts/ToastContext';
import { sanitizeInput } from '../config/security';
import BookingForm from '../components/BookingForm';
import SearchFilter from '../components/SearchFilter';
import BulkActions from '../components/BulkActions';
import ExportData from '../components/ExportData';
import StatusBadge from '../components/StatusBadge';
import { exportSingleRecord } from '../utils/pdfExport';
import PageTransition from '../components/PageTransition';
import LoadingSpinner from '../components/LoadingSpinner';
import notificationSound from '../utils/notificationSound';

export default function Bookings() {
  const { isDark } = useTheme();
  const { t, language } = useLanguage();
  const toast = useToastContext();
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [selectedBookings, setSelectedBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editBooking, setEditBooking] = useState(null);
  const [viewMode, setViewMode] = useState('table');

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/bookings');
      const bookingsData = response.data?.data || response.data || [];

      // Check for new bookings
      if (bookings.length > 0 && bookingsData.length > bookings.length) {
        notificationSound.playBookingNotification();
      }

      setBookings(bookingsData);
      setFilteredBookings(bookingsData);
    } catch (err) {
      setError('Gagal memuat data booking');
      console.error('Error fetching bookings:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings();

    // Auto-refresh every 30 seconds to check for new bookings
    const interval = setInterval(fetchBookings, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleDeleteBooking = async (bookingId) => {
    if (!window.confirm(t('confirm_delete_booking'))) return;

    try {
      if (!bookingId || (typeof bookingId !== 'number' && typeof bookingId !== 'string')) {
        throw new Error('ID booking tidak valid');
      }

      const sanitizedId = encodeURIComponent(String(bookingId));
      await api.delete(`/bookings/${sanitizedId}`);

      fetchBookings();
      toast.success(t('booking_deleted_success'));
    } catch (err) {
      console.error('Error deleting booking:', err);
      toast.error(t('failed_delete_booking'));
    }
  };

  const handleEditBooking = (booking) => {
    setEditBooking(booking);
    setShowForm(true);
  };

  const handleFormSave = () => {
    setShowForm(false);
    setEditBooking(null);
    fetchBookings();
  };

  const handleSearch = (searchTerm) => {
    const filtered = bookings.filter(booking =>
      booking.user?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.property?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.status.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredBookings(filtered);
  };

  const handleFilter = (filterValue) => {
    if (!filterValue) {
      setFilteredBookings(bookings);
    } else {
      const filtered = bookings.filter(booking => booking.status === filterValue);
      setFilteredBookings(filtered);
    }
  };

  const handleBulkAction = async (action, selectedIds) => {
    if (action === 'delete') {
      if (confirm(`${t('delete')} ${selectedIds.length} ${t('selected')} ${t('bookings')}?`)) {
        try {
          await Promise.all(selectedIds.map(id => api.delete(`/bookings/${id}`)));
          fetchBookings();
          setSelectedBookings([]);
        } catch (error) {
          console.error('Bulk delete failed:', error);
        }
      }
    }
  };

  const handleBookingSelect = (bookingId) => {
    setSelectedBookings(prev =>
      prev.includes(bookingId)
        ? prev.filter(id => id !== bookingId)
        : [...prev, bookingId]
    );
  };

  const handleStatusUpdate = async (id, status) => {
    try {
      await api.put(`/bookings/${id}`, { status });
      fetchBookings();
    } catch (error) {
      console.error('Error updating booking status:', error);
    }
  };

  const bulkActions = [
    { key: 'delete', label: t('delete'), className: 'bg-red-500 text-white hover:bg-red-600' }
  ];

  const statusFilters = [
    { value: 'pending', label: t('pending') },
    { value: 'approved', label: t('approved') },
    { value: 'active', label: t('active') },
    { value: 'completed', label: t('completed') },
    { value: 'cancelled', label: t('cancelled') }
  ];

  if (loading) {
    return (
      <PageTransition>
        <div className="flex items-center justify-center min-h-96">
          <LoadingSpinner variant="bookings" size="large" />
        </div>
      </PageTransition>
    );
  }

  if (error) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className={`${isDark ? 'bg-gray-800/95 border-gray-700/20' : 'bg-white/95 border-white/20'} backdrop-blur-xl rounded-2xl shadow-xl border p-8 text-center`}
      >
        <div className="text-6xl mb-4 text-red-500">⚠️</div>
        <h2 className={`text-xl font-semibold mb-2 ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
          {t('error_loading_data')}
        </h2>
        <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{error}</p>
        <button
          onClick={fetchBookings}
          className={`px-6 py-2 rounded-lg transition-colors ${isDark ? 'bg-gray-600 text-gray-100 hover:bg-gray-500' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
        >
          {t('try_again')}
        </button>
      </motion.div>
    );
  }

  return (
    <PageTransition>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="space-y-6"
      >
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`${isDark ? 'bg-gray-800/95 border-gray-700/20' : 'bg-white/95 border-white/20'} backdrop-blur-xl rounded-2xl shadow-xl border p-6`}
        >
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
            <div>
              <h1 className={`text-3xl font-bold ${isDark ? 'text-gray-100' : 'bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent'}`}>
                {t('booking_management')}
              </h1>
              <p className={`mt-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                {t('manage_all_bookings')}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
              <ExportData data={filteredBookings} filename="bookings" language={language} />
              <button
                onClick={() => setShowForm(true)}
                className={`${isDark ? 'bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800' : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800'} text-white px-6 py-3 rounded-xl font-medium transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center`}
              >
                <i className="fas fa-plus mr-2"></i>
                {t('add_booking')}
              </button>
            </div>
          </div>
        </motion.div>

        {/* Controls */}
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <SearchFilter
              onSearch={handleSearch}
              onFilter={handleFilter}
              filters={statusFilters}
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">{t('view')}:</span>
            <button
              onClick={() => setViewMode('cards')}
              className={`px-3 py-2 rounded-lg text-sm transition-all ${viewMode === 'cards'
                  ? (isDark ? 'bg-gray-600 text-white' : 'bg-blue-600 text-white')
                  : (isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-600 hover:bg-gray-300')
                }`}
            >
              <i className="fas fa-th-large mr-1"></i> {t('cards')}
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`px-3 py-2 rounded-lg text-sm transition-all ${viewMode === 'table'
                  ? (isDark ? 'bg-gray-600 text-white' : 'bg-blue-600 text-white')
                  : (isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-600 hover:bg-gray-300')
                }`}
            >
              <i className="fas fa-table mr-1"></i> {t('table')}
            </button>
          </div>
        </div>

        <BulkActions
          selectedItems={selectedBookings}
          onAction={handleBulkAction}
          actions={bulkActions}
        />

        {/* Content */}
        <AnimatePresence mode="wait">
          {viewMode === 'cards' ? (
            // Cards View
            <motion.div
              key="cards"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {filteredBookings.map((booking, index) => (
                <motion.div
                  key={booking.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ scale: 1.05, y: -5 }}
                  className={`${isDark ? 'bg-gray-800/95 border-gray-700/20' : 'bg-white/95 border-white/20'} backdrop-blur-xl rounded-2xl shadow-xl border p-6 hover:shadow-2xl transition-all duration-300 h-96 flex flex-col`}
                >
                  {/* Header */}
                  <div className="flex justify-between items-start mb-4">
                    <input
                      type="checkbox"
                      checked={selectedBookings.includes(booking.id)}
                      onChange={() => handleBookingSelect(booking.id)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <StatusBadge
                      currentStatus={booking.status}
                      statusOptions={['pending', 'approved', 'active', 'completed', 'cancelled']}
                      onStatusChange={(newStatus) => handleStatusUpdate(booking.id, newStatus)}
                    />
                  </div>

                  {/* Booking Info */}
                  <div className="mb-4 flex-1">
                    <h3 className={`text-lg font-bold mb-3 ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{t('booking')} #{booking.id}</h3>
                    <div className="space-y-3 text-sm">
                      <p className={`flex items-center ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                        <i className={`fas fa-user mr-2 ${isDark ? 'text-gray-400' : 'text-blue-500'}`}></i>
                        <span className="truncate">{sanitizeInput(booking.user?.name || `User #${booking.user_id}`)}</span>
                      </p>
                      <p className={`flex items-center ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                        <i className={`fas fa-building mr-2 ${isDark ? 'text-gray-400' : 'text-blue-500'}`}></i>
                        <span className="truncate">{sanitizeInput(booking.property?.name || `Property #${booking.property_id}`)}</span>
                      </p>
                      <p className={`flex items-center ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                        <i className={`fas fa-calendar-alt mr-2 ${isDark ? 'text-gray-400' : 'text-blue-500'}`}></i>
                        <span className="truncate">{new Date(booking.start_date).toLocaleDateString(language === 'id' ? 'id-ID' : 'en-US')} - {new Date(booking.end_date).toLocaleDateString(language === 'id' ? 'id-ID' : 'en-US')}</span>
                      </p>
                      <p className={`flex items-center font-semibold text-lg p-3 rounded-lg ${isDark ? 'text-gray-300 bg-gray-700/50' : 'text-green-600 bg-green-50'}`}>
                        <i className={`fas fa-money-bill-wave mr-2 ${isDark ? 'text-gray-400' : ''}`}></i>
                        Rp {Number(booking.total_amount || 0).toLocaleString()}
                      </p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEditBooking(booking)}
                      className={`flex-1 text-white py-2 px-3 rounded-lg text-sm font-medium transition-all duration-200 shadow-md hover:shadow-lg ${isDark ? 'bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800' : 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700'}`}
                    >
                      <i className="fas fa-edit mr-1"></i> {t('edit')}
                    </button>
                    <button
                      onClick={() => exportSingleRecord(booking, 'booking', 'booking', language)}
                      className={`text-white py-2 px-3 rounded-lg text-sm font-medium transition-all duration-200 shadow-md hover:shadow-lg ${isDark ? 'bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800' : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800'}`}
                      title={t('export_details')}
                    >
                      <i className="fas fa-file-pdf"></i>
                    </button>
                    <button
                      onClick={() => handleDeleteBooking(booking.id)}
                      className="bg-gradient-to-r from-gray-500 to-gray-600 text-white py-2 px-3 rounded-lg text-sm font-medium hover:from-red-500 hover:to-red-600 transition-all duration-200 shadow-md hover:shadow-lg"
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          ) : (
            <motion.div
              key="table"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className={`${isDark ? 'bg-gray-800/95 border-gray-700/20' : 'bg-white/95 border-white/20'} backdrop-blur-xl rounded-2xl shadow-xl border overflow-hidden`}
            >
              <div className="overflow-x-auto">
                <table className="w-full min-w-[800px]">
                  <thead className={isDark ? 'bg-gradient-to-r from-gray-700 to-gray-600' : 'bg-gradient-to-r from-slate-50 to-blue-50'}>
                    <tr>
                      <th className="px-6 py-4 text-left">
                        <input
                          type="checkbox"
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedBookings(filteredBookings.map(b => b.id));
                            } else {
                              setSelectedBookings([]);
                            }
                          }}
                          checked={selectedBookings.length === filteredBookings.length && filteredBookings.length > 0}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </th>
                      <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${isDark ? 'text-gray-200' : 'text-slate-700'}`}>{t('id')}</th>
                      <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${isDark ? 'text-gray-200' : 'text-slate-700'}`}>{t('user')}</th>
                      <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${isDark ? 'text-gray-200' : 'text-slate-700'}`}>{t('property')}</th>
                      <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${isDark ? 'text-gray-200' : 'text-slate-700'}`}>{t('dates')}</th>
                      <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${isDark ? 'text-gray-200' : 'text-slate-700'}`}>{t('guests')}</th>
                      <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${isDark ? 'text-gray-200' : 'text-slate-700'}`}>{t('amount')}</th>
                      <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${isDark ? 'text-gray-200' : 'text-slate-700'}`}>{t('status')}</th>
                      <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${isDark ? 'text-gray-200' : 'text-slate-700'}`}>{t('actions')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredBookings.map((booking, index) => (
                      <tr key={booking.id} className={`transition-all duration-200 ${isDark ? (selectedBookings.includes(booking.id) ? 'bg-gray-700/50' : index % 2 === 0 ? 'bg-gray-800/30' : 'bg-gray-700/30') + ' hover:bg-gray-600/50' : (selectedBookings.includes(booking.id) ? 'bg-gradient-to-r from-blue-50 to-purple-50' : index % 2 === 0 ? 'bg-white/50' : 'bg-slate-50/50') + ' hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50'}`}>
                        <td className="px-6 py-4">
                          <input
                            type="checkbox"
                            checked={selectedBookings.includes(booking.id)}
                            onChange={() => handleBookingSelect(booking.id)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                        </td>
                        <td className={`px-6 py-4 text-sm font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>#{booking.id}</td>
                        <td className={`px-6 py-4 text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{sanitizeInput(booking.user?.name || `User #${booking.user_id}`)}</td>
                        <td className={`px-6 py-4 text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{sanitizeInput(booking.property?.name || `Property #${booking.property_id}`)}</td>
                        <td className={`px-6 py-4 text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{new Date(booking.start_date).toLocaleDateString(language === 'id' ? 'id-ID' : 'en-US')} - {new Date(booking.end_date).toLocaleDateString(language === 'id' ? 'id-ID' : 'en-US')}</td>
                        <td className={`px-6 py-4 text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                          <span className="flex items-center">
                            <i className="fas fa-users mr-2 text-blue-500"></i>
                            {booking.guests || 1} {language === 'id' ? 'orang' : 'guest(s)'}
                          </span>
                        </td>
                        <td className={`px-6 py-4 text-sm font-bold ${isDark ? 'text-gray-300' : 'text-green-600'}`}>Rp {Number(booking.total_amount || 0).toLocaleString()}</td>
                        <td className="px-6 py-4">
                          <StatusBadge
                            currentStatus={booking.status}
                            statusOptions={['pending', 'approved', 'active', 'completed', 'cancelled']}
                            onStatusChange={(newStatus) => handleStatusUpdate(booking.id, newStatus)}
                          />
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex gap-1">
                            <button
                              onClick={() => handleEditBooking(booking)}
                              className={`text-white px-2 py-1 rounded-lg text-xs font-medium transition-all duration-200 ${isDark ? 'bg-gray-600 hover:bg-gray-700' : 'bg-blue-500 hover:bg-blue-600'}`}
                            >
                              {t('edit')}
                            </button>
                            <button
                              onClick={() => exportSingleRecord(booking, 'booking', 'booking', language)}
                              className={`text-white px-2 py-1 rounded-lg text-xs font-medium transition-all duration-200 ${isDark ? 'bg-gray-600 hover:bg-gray-700' : 'bg-blue-600 hover:bg-blue-700'}`}
                              title={t('export_details')}
                            >
                              <i className="fas fa-file-pdf"></i>
                            </button>
                            <button
                              onClick={() => handleDeleteBooking(booking.id)}
                              className="bg-gray-500 text-white px-2 py-1 rounded-lg text-xs font-medium hover:bg-red-600 transition-all duration-200"
                            >
                              {t('delete')}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {filteredBookings.length === 0 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className={`${isDark ? 'bg-gray-800/95 border-gray-700/20' : 'bg-white/95 border-white/20'} backdrop-blur-xl rounded-2xl shadow-xl border p-12 text-center`}
          >
            <div className={`text-6xl mb-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              📅
            </div>
            <h3 className={`text-xl font-semibold mb-2 ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
              {t('no_bookings_found')}
            </h3>
            <p className={isDark ? 'text-gray-300' : 'text-gray-600'}>
              {t('no_bookings_available')}
            </p>
          </motion.div>
        )}

        <AnimatePresence>
          {showForm && (
            <BookingForm
              key={editBooking?.id || 'new'}
              booking={editBooking}
              onSave={handleFormSave}
              onCancel={() => { setShowForm(false); setEditBooking(null); }}
            />
          )}
        </AnimatePresence>
      </motion.div>
    </PageTransition>
  );
}